import { forwardRef, type SelectHTMLAttributes } from 'react'

import { cn } from '@/lib/cn'

export interface SelectOption {
  value: string
  label: string
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  options: SelectOption[]
  placeholder?: string
  selectSize?: 'sm' | 'md'
}

/**
 * A native `<select>` wearing the app's control styling.
 *
 * Native on purpose: on a phone this opens the platform picker, which is both
 * faster and more accessible than any listbox I would write, and this app is
 * used on phones in the field.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, placeholder, selectSize = 'md', className, ...props },
  ref,
) {
  return (
    <div className="relative">
      <select
        ref={ref}
        className={cn(
          'w-full appearance-none rounded-md border border-line bg-surface-raised shadow-xs',
          'text-ink transition-[border-color] duration-100 ease-out hover:border-line-strong',
          'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-ink-muted',
          selectSize === 'sm' ? 'h-8 pr-7 pl-2.5 text-sm' : 'h-9 pr-8 pl-3 text-base',
          className,
        )}
        {...props}
      >
        {placeholder ? <option value="">{placeholder}</option> : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <svg
        viewBox="0 0 16 16"
        className="pointer-events-none absolute top-1/2 right-2.5 h-3.5 w-3.5 -translate-y-1/2 text-ink-muted"
        aria-hidden="true"
      >
        <path
          d="M4 6l4 4 4-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    </div>
  )
})
